import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Banknote, CheckCircle, ExternalLink, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, getToken } from "@/lib/api";

const PROXY_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/mobile-proxy`;

// Call the mobile backend THROUGH our own server (no browser CORS limits).
// Returns { status, location, body } from the upstream mobile backend.
async function proxyCall(method, path) {
  const token = getToken();
  const res = await fetch(`${PROXY_BASE}/${path}`, {
    method,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  const data = await res.json();
  return { status: data.upstream_status, location: data.upstream_location, body: data.upstream_body };
}

function pickUrl(body, location) {
  if (location) return location;
  if (body && typeof body === "object") {
    return body.url || body.onboarding_url || body.account_link_url || body.link || body.dashboard_url || null;
  }
  return null;
}

export default function OnboardBank() {
  const navigate = useNavigate();
  const hasToken = !!getToken();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [debug, setDebug] = useState(null);
  const [diag, setDiag] = useState(null);
  const [diagRunning, setDiagRunning] = useState(false);

  const probe = async (method, path) => {
    try {
      const res = method === "post" ? await api.post(path) : await api.get(path);
      return { path, status: res.status, ok: true, body: res.data };
    } catch (e) {
      return {
        path,
        status: e?.response?.status ?? null,
        ok: false,
        body: e?.response?.data ?? null,
        message: e?.response ? null : e?.message || "no response",
      };
    }
  };

  const rawOnboardingProbe = async () => {
    const base = process.env.REACT_APP_API_BASE;
    const token = getToken();
    const out = { path: "POST stripe-onboarding (redirect check)" };
    try {
      const res = await fetch(`${base}/recipients/me/stripe-onboarding`, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        redirect: "manual",
      });
      out.type = res.type;
      out.status = res.status;
      if (res.type === "opaqueredirect") {
        out.verdict = "BACKEND RETURNS A REDIRECT (302) → this is the bug. Must return JSON {url} instead.";
      } else {
        try { out.body = (await res.text())?.slice(0, 300); } catch (_) {}
        out.verdict = `Not a redirect (type=${res.type}, status=${res.status}).`;
      }
    } catch (e) {
      out.type = "fetch-error";
      out.message = e?.message || String(e);
      out.verdict = "No redirect detected but request errored → likely a 500 whose error response is missing CORS headers.";
    }
    return out;
  };

  const runDiagnostic = async () => {
    setDiagRunning(true);
    setDiag(null);
    const results = [];
    results.push(await probe("get", "/auth/me"));
    results.push(await probe("get", "/recipients/me/stripe-status"));
    results.push(await probe("post", "/recipients/me/stripe-onboarding"));
    results.push(await rawOnboardingProbe());
    setDiag(results);
    setDiagRunning(false);
  };

  const [variants, setVariants] = useState(null);
  const [variantsRunning, setVariantsRunning] = useState(false);

  const tryVariants = async () => {
    setVariantsRunning(true);
    setVariants(null);
    const base = process.env.REACT_APP_API_BASE;
    const token = getToken();
    const origin = window.location.origin;
    const ret = `${origin}/onboard`;
    const ref = `${origin}/onboard`;
    const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

    const cases = [
      { label: "A) empty body (current web behavior)", url: "/recipients/me/stripe-onboarding", body: null },
      { label: "B) return_url + refresh_url (JSON)", url: "/recipients/me/stripe-onboarding", body: { return_url: ret, refresh_url: ref } },
      { label: "C) camelCase returnUrl/refreshUrl", url: "/recipients/me/stripe-onboarding", body: { returnUrl: ret, refreshUrl: ref } },
      { label: "D) platform:web + urls", url: "/recipients/me/stripe-onboarding", body: { platform: "web", return_url: ret, refresh_url: ref } },
      { label: "E) urls as query params", url: `/recipients/me/stripe-onboarding?return_url=${encodeURIComponent(ret)}&refresh_url=${encodeURIComponent(ref)}`, body: null },
    ];

    const results = [];
    for (const c of cases) {
      const r = { label: c.label };
      try {
        const res = await fetch(`${base}${c.url}`, {
          method: "POST",
          headers: c.body ? { ...authHeaders, "Content-Type": "application/json" } : authHeaders,
          body: c.body ? JSON.stringify(c.body) : undefined,
        });
        r.status = res.status;
        let data = null;
        try { data = await res.json(); } catch (_) { try { data = await res.text(); } catch (__) {} }
        const link = data && typeof data === "object" ? (data.url || data.onboarding_url || data.account_link_url || data.link) : null;
        if (res.ok && link) { r.result = "✅ SUCCESS — got a Stripe URL"; r.url = link; }
        else { r.result = `status ${res.status}`; r.server = typeof data === "string" ? data.slice(0, 200) : JSON.stringify(data).slice(0, 200); }
      } catch (e) {
        r.status = "(blocked / network error)";
        r.result = "❌ crashed (500 without CORS, browser blocked)";
        r.server = e?.message || String(e);
      }
      results.push(r);
    }
    setVariants(results);
    setVariantsRunning(false);
  };

  const loadStatus = () => {
    setLoading(true);
    api
      .get("/recipients/me/stripe-status")
      .then(({ data }) => setStatus(data))
      .catch(() => setStatus(null))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadStatus();
  }, []);

  const onboarded = status?.charges_enabled || status?.payouts_enabled || status?.onboarded || status?.details_submitted;

  const startOnboarding = async () => {
    setWorking(true);
    setDebug(null);
    try {
      const origin = window.location.origin; // always https on preview + production
      const payload = {
        return_url: `${origin}/onboard`,
        refresh_url: `${origin}/onboard`,
        returnUrl: `${origin}/onboard`,
        refreshUrl: `${origin}/onboard`,
      };
      const { data } = await api.post("/recipients/me/stripe-onboarding", payload);
      const url = pickUrl(data, null);
      if (url) {
        window.location.href = url;
      } else {
        toast.error("Onboarding link unavailable.");
        setDebug({ where: "stripe-onboarding", kind: "no-url", status: 200, body: data });
      }
    } catch (e) {
      const st = e?.response?.status;
      const detail = e?.response?.data?.detail;
      toast.error(detail ? `Onboarding failed: ${detail}` : `Could not start onboarding (${st || "error"}).`);
      setDebug({ where: "stripe-onboarding", kind: "error", status: st || null, body: e?.response?.data || null, message: e?.message });
    } finally {
      setWorking(false);
    }
  };

  const openLink = async (path, label) => {
    setDebug(null);
    try {
      const { data } = await api.post(path);
      const url = pickUrl(data, null);
      if (url) {
        window.open(url, "_blank", "noopener");
      } else {
        toast.error(`${label} unavailable.`);
        setDebug({ where: path, kind: "no-url", status: 200, body: data });
      }
    } catch (e) {
      const st = e?.response?.status;
      const detail = e?.response?.data?.detail;
      toast.error(detail ? `${label}: ${detail}` : `Could not open ${label} (${st || "error"}).`);
      setDebug({ where: path, kind: "error", status: st || null, body: e?.response?.data || null, message: e?.message });
    }
  };

  return (
    <div className="max-w-lg mx-auto px-4 py-5 pb-28">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm font-bold text-ink-tertiary mb-4">
        <ArrowLeft size={18} /> Back
      </button>

      <div className="flex items-center gap-3 mb-2">
        <div className="w-12 h-12 rounded-pill grid place-items-center text-white" style={{ background: "linear-gradient(135deg,#1E40AF,#5B21B6)" }}>
          <Banknote size={24} />
        </div>
        <h1 className="text-2xl font-black">Bank & payouts</h1>
      </div>
      <p className="text-sm text-ink-tertiary font-semibold mb-6">Connect a Stripe Express account to receive your blessings as real payouts.</p>

      {loading ? (
        <div className="py-12 grid place-items-center"><Loader2 className="animate-spin text-brand-primary" size={30} /></div>
      ) : onboarded ? (
        <div className="space-y-4">
          <div className="flex items-center gap-3 bg-surface-2 border border-border rounded-md p-5">
            <CheckCircle className="text-successb" size={28} />
            <div>
              <div className="font-black">You're all set!</div>
              <div className="text-sm text-ink-tertiary">Payouts are enabled on your account.</div>
            </div>
          </div>
          <Button data-testid="stripe-dashboard-link" onClick={() => openLink("/recipients/me/stripe-dashboard-link", "dashboard")} className="w-full h-12 rounded-pill font-extrabold bg-brand-primary text-white">
            Open Stripe dashboard <ExternalLink size={16} className="ml-1" />
          </Button>
          <Button data-testid="stripe-payout-details-link" variant="outline" onClick={() => openLink("/recipients/me/stripe-payout-details-link", "payout details")} className="w-full h-12 rounded-pill font-extrabold border-border">
            Update payout details <ExternalLink size={16} className="ml-1" />
          </Button>
        </div>
      ) : (
        <Button data-testid="start-onboarding-button" disabled={working} onClick={startOnboarding} className="w-full h-14 rounded-pill font-black bg-brand-primary text-white">
          {working ? <Loader2 className="animate-spin" size={20} /> : <>Connect with Stripe <ExternalLink size={16} className="ml-1" /></>}
        </Button>
      )}

      {debug && (
        <div data-testid="onboard-debug" className="mt-6 rounded-md border border-border bg-surface-2 p-4 text-xs font-mono break-words whitespace-pre-wrap">
          <div className="font-black text-sm mb-2 text-red-500">Diagnostic info (screenshot this)</div>
          <div><b>request:</b> {debug.where}</div>
          <div><b>type:</b> {debug.kind}</div>
          <div><b>http status:</b> {debug.status ?? "(none — request blocked before a response)"}</div>
          <div><b>signed in:</b> {hasToken ? "yes (token present)" : "NO (no token stored)"}</div>
          {debug.message && <div><b>message:</b> {debug.message}</div>}
          {debug.body != null && <div><b>server said:</b> {JSON.stringify(debug.body)}</div>}
        </div>
      )}

      <div className="mt-8 border-t border-border pt-6">
        <Button data-testid="run-diagnostic-button" variant="outline" disabled={diagRunning} onClick={runDiagnostic} className="w-full h-11 rounded-pill font-extrabold border-border">
          {diagRunning ? <Loader2 className="animate-spin" size={18} /> : "Run full diagnostic"}
        </Button>
        {diag && (
          <div data-testid="full-diagnostic" className="mt-4 rounded-md border border-border bg-surface-2 p-4 text-xs font-mono break-words whitespace-pre-wrap space-y-3">
            <div className="font-black text-sm text-red-500">Full diagnostic (screenshot this)</div>
            <div>signed in (token present): <b>{hasToken ? "yes" : "NO"}</b></div>
            {diag.map((r, i) => (
              <div key={i} className="border-t border-border pt-2">
                <div><b>{r.path}</b></div>
                {r.type && <div>type: {r.type}</div>}
                <div>status: {r.status ?? "(none — blocked before response / CORS)"}</div>
                {r.message && <div>message: {r.message}</div>}
                {r.body != null && <div>server: {JSON.stringify(r.body).slice(0, 300)}</div>}
                {r.verdict && <div className="text-red-500 font-bold mt-1">→ {r.verdict}</div>}
              </div>
            ))}
          </div>
        )}

        <Button data-testid="try-variants-button" variant="outline" disabled={variantsRunning} onClick={tryVariants} className="w-full h-11 rounded-pill font-extrabold border-border mt-4">
          {variantsRunning ? <Loader2 className="animate-spin" size={18} /> : "Try onboarding payload variations"}
        </Button>
        {variants && (
          <div data-testid="variants-diagnostic" className="mt-4 rounded-md border border-border bg-surface-2 p-4 text-xs font-mono break-words whitespace-pre-wrap space-y-3">
            <div className="font-black text-sm text-green-600">Payload test (screenshot this)</div>
            {variants.map((r, i) => (
              <div key={i} className="border-t border-border pt-2">
                <div className="font-bold">{r.label}</div>
                <div>{r.result}</div>
                {r.url && <div className="text-green-600 break-all">url: {r.url.slice(0, 120)}…</div>}
                {r.server && <div>server: {r.server}</div>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
